/**
 * Today — the screen staff live in (design reference v4, s-today). The day's positions 1..capacity
 * (+ the surge position), each with the person, the period, the status and its tags and Call (1.8.3: no
 * email and no phone number is printed here, for any role); "Happening now" and "Next"; pending close-outs at the top;
 * facility spaces after the positions; then the look-ahead and Needs your attention. Each visit has its
 * Queue 1 actions and each open position a Book button (0.3.0), with the Pending badge and the processor's
 * answer on the row; the six-visit warning shows for the day. Finish opens the close-out (0.4.0): a closed
 * visit shows who closed it (and "edited by"), with Edit and, after a late edit, Send update; staff units
 * and facility spaces take "Mark completed" instead (no completion record).
 *
 * 1.5.15: a press that is with the scheduler (Finish, Arrived, Confirm by phone, Move, Cancel) shows one line where
 * its button was — "Closing out… the scheduler records it in a few minutes" for Finish — from the press itself and
 * from the server's list of open requests (after a reload, on another device); a close-out that is waiting is not
 * in "visits to close out".
 *
 * The crew sees only their visits for today, Call and Finish — nothing else.
 *
 * 1.5.13: `/?day=yyyyMMdd` for a day before today shows that day as a record (./today/PastDay.tsx) — every visit
 * on record with how it ended, nothing to press; the day buttons in the head go back a day and return to today.
 * `/?book=1` (the typed address /book) opens the Book sheet. A proposal in pencil reads "Waiting for confirmation".
 *
 * 1.5.16: the crew's card has two controls — Call, and one button: Arrived, which after an arrival is Finish. The
 * office's work (Move, hold, cancel, confirm by phone, Tenant won't be home, permission by phone…) is under one
 * "More ⋯", never shown to the crew. A permission to enter on file stands above the buttons, for everyone.
 *
 * 1.6.6: two crews on one day. The office sees two lanes, Crew A and Crew B, each on its own clock (side by side on
 * a wide screen, stacked on a phone, A first). A crew member sees their own lane first, then the other crew's visits
 * greyed and locked — "Crew B — not yours": no Arrived, no Finish, no Mark completed, no Call (the server refuses
 * them all the same). With no crew yet, every card is locked: "Ask the office which crew you are on today".
 *
 * 1.5.17: "More ⋯" is "Office action" — what a tenant asks the office for by phone — and only an administrator or a
 * co-administrator has it (not the scheduler, not the crew). A proposal's "Waiting for confirmation" is the schedule's
 * own press and stays the scheduler's too. The card says whether a permission to enter is on file either way:
 * "Permission to enter: received … by …" or "Permission to enter: not on file".
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useMessages } from '@/lib/messages';
import type { TodayVisit } from '@shared/api-types';
import { clockWithPeriod, formatClock } from '@shared/dayplan';
import { isDateKey, serviceMinutesNow } from '@shared/dates';
import { formatDayKey, formatClockOf, greetingKey, serviceDateKey } from '@/lib/dates';
import { useToday } from '@/lib/queries';
import { readTodayCopy, writeTodayCopy } from '@/lib/today-cache';
import { CHANGED_MARK_MS, changedBetween, useVisibleInterval } from '@/lib/live-refresh';
import { LIVE_REFRESH_MS, liveRefreshOn } from '@shared/crew-1102';
import { useOfflineQueue } from '@/features/offline/OfflineQueueProvider';
import { WaitingToSend } from '@/features/offline/WaitingToSend';
import { prefetchFinishConfig } from '@/features/offline/prefetch';
import { useDayLayout } from '@/lib/useTimes';
import { lengthWords } from '@shared/dayplan';
import { isLiveOnDay, periodAt, takesCompletionRecord } from '@shared/visits';
import { TechTodayPage } from '@/features/tech/TechToday';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { StatusBadge, appointmentStatusKey, type StatusKey } from '@/components/StatusBadge';
import { CallButton, CallLink } from '@/components/Contact';
import { Updated } from '@/components/Updated';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Me } from '@/auth/useMe';
import { AttentionCard } from './today/Attention';
import { useBooking } from '@/features/booking/BookingProvider';
import { ClosedWaitingLine } from '@/pages/today/ClosedWaitingLine';
import { useFinish } from '@/features/finish/FinishProvider';
import { ArrivedSheet, PermissionWords } from '@/features/arrival/ArrivedSheet';
import { startsVisit } from '@shared/arrival';
import { Sheet } from '@/components/ui/sheet';
import { devicePresses, useTx } from '@/lib/tx';
import { dayAwaitsCloseout } from '@shared/closeout-rule';
import { CLOSEOUT_LATE_SETTING, closeoutLate, closeoutLateAfter, closingOut, openPresses, stalePress } from '@shared/with-scheduler';
import { WithScheduler } from '@/components/WithScheduler';
import { useDayPlan } from '@/lib/queries';
import { bookingSetting } from '@shared/booking';
import { OutcomeLine, PendingBadge, RecentRequests } from '@/components/TxStatus';
import { LookAhead } from './today/LookAhead';
import { ExtraSpotsCard } from '@/features/extra/OpenSpots';
import { DayNav, PastDayPage } from './today/PastDay';
import { useMeExtras } from '@/lib/admin';
import { OFFICE_ROLES } from '@shared/office-action';
import { CrewTag, PressLines, useNow } from '@/features/press/Press';
import { openTooLong, openTooLongMinutes, pressName } from '@shared/press';
import { ReviewVisitLink } from '@/features/review/ReviewVisitLink';
import { canReviewVisit } from '@shared/review';
import { LANES, TWO_CREW_WARN_AT, lanePositions } from '@shared/day-crews';
import type { BendLane, FreeGap } from '@shared/day-bend';
import { bendStateOf, useDayBend } from '@/features/daybend/day';
import { MoveInDaySheet } from '@/features/daybend/MoveInDaySheet';
import { MenuButton, type MenuItem } from '@/components/ui/menu-button';
import { useCallLink } from '@/lib/calling';
import { REVIEW_ROW_ROLES, reviewPath } from '@shared/review';
import { availableActions, moreActionsFor, primaryActionFor, type RowFacts, type RowPrimary } from './today/primaryAction';

const OPEN_STATUSES = new Set(['Scheduled', 'In Progress']);
/** 1.6.8: before or during the visit — the card's secondary button is Office action; after it, Review visit. */
const BEFORE_OR_DURING = new Set(['Proposed', 'Scheduled', 'On Hold', 'In Progress']);
const ON_POSITION = new Set(['Scheduled', 'On Hold', 'In Progress', 'Completed', 'No Show', 'Unable to Complete']);
/** 1.7.8: an unfinished card that holds a time — the ones Move is for. */
const MOVABLE = new Set(['Scheduled', 'On Hold', 'In Progress']);

export function visitName(v: { preferredName?: string | null; tenantName: string }): string {
  return v.preferredName ? `${v.preferredName} (${v.tenantName})` : v.tenantName;
}

/** The statuses a Reopen takes (the scheduler checks it again): a visit that was closed. */
const REOPENABLE = ['Completed', 'No Show', 'Unable to Complete'];

/** The extra tags a row carries besides its status (surge · staff · facility · not yet confirmed). */
export function visitTags(v: Pick<TodayVisit, 'surge' | 'kind' | 'status' | 'confirmation'>): StatusKey[] {
  const tags: StatusKey[] = [];
  if (v.surge) tags.push('surge');
  if (v.kind === 'staff') tags.push('staff');
  if (v.kind === 'facility') tags.push('facility');
  if (v.status === 'Scheduled' && v.kind === 'tenant' && v.confirmation !== 'Confirmed') tags.push('awaiting');
  return tags;
}

/** 19 Oct 2026 (D-1019-ATTN): a Tech Support visit — not Housekeeping's, though it shared the booking system. */
const isTechVisit = (v: Pick<TodayVisit, 'program'>) => (v.program ?? '').toUpperCase() === 'TECH';

/**
 * While the stored copy of Today is on the screen, a press on anything that acts (a button, a link, a menu, a field) is
 * stopped where it starts and a polite line says why. Reading is not stopped: the copy can be looked at and scrolled.
 */
const ACTS = 'button, a[href], [role="button"], [role="menuitem"], input, select, textarea, summary';
function staleGuards(say: () => void) {
  const stop = (e: { target: EventTarget | null; preventDefault: () => void; stopPropagation: () => void }) => {
    const el = e.target instanceof Element ? e.target.closest(ACTS) : null;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    say();
  };
  return {
    onClickCapture: stop,
    onPointerDownCapture: stop,
    onKeyDownCapture: (e: { key: string; target: EventTarget | null; preventDefault: () => void; stopPropagation: () => void }) => {
      if (e.key === 'Enter' || e.key === ' ') stop(e);
    },
  };
}

export function TodayPage({ me }: { me: Me }) {
  // the technology associate (1.5.0): their own tickets only
  if (me.role === 'tech') return <TechTodayPage me={me} />;
  return <HouseTodayPage me={me} />;
}

/** Today — or, for `?day=` before today, that day as a record. A day that is not before today is today. */
function HouseTodayPage({ me }: { me: Me }) {
  const [params] = useSearchParams();
  const today = serviceDateKey();
  const asked = params.get('day');
  if (asked && isDateKey(asked) && asked < today) return <PastDayPage me={me} day={asked} today={today} />;
  return <LiveTodayPage me={me} />;
}

function LiveTodayPage({ me }: { me: Me }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const day = serviceDateKey();
  const q0 = useToday(day);
  const isCrew = me.role === 'crew';
  // 1 Nov 2026 (D-1102-CREW-1): the last Today this device loaded (this person, this day) shows at once while the live
  // read is on its way; the live read replaces it, and nothing is pressed from it
  const user = me.principal?.userDetails ?? null;
  const [stored] = useState(() => readTodayCopy(user, day));
  const fromCopy = !q0.data && !!stored;
  const q = {
    data: fromCopy ? stored!.data : q0.data,
    dataUpdatedAt: fromCopy ? stored!.savedAt : q0.dataUpdatedAt,
    isLoading: fromCopy ? false : q0.isLoading,
    // a read that fails while there is something to show leaves it showing (the one-minute reads would otherwise blank it)
    isError: fromCopy ? false : q0.isError && !q0.data,
    error: q0.error,
    isFetching: fromCopy ? false : q0.isFetching,
    refetch: q0.refetch,
  };
  useEffect(() => {
    if (q0.data && !q0.isPlaceholderData) writeTodayCopy(user, day, q0.data, q0.dataUpdatedAt || Date.now());
  }, [q0.data, q0.dataUpdatedAt, q0.isPlaceholderData, user, day]);
  const crewExtras = useMeExtras(isCrew);
  const staff = !!me.role && (OFFICE_ROLES as readonly string[]).includes(me.role);
  const finish = useFinish();
  const booking = useBooking();
  const txs = useTx();
  const dayPlan = useDayPlan();
  // 1.6.5: the stamps are another phone's presses too — read the day again whenever the app comes to the front
  const refetchToday = q.refetch;
  useEffect(() => {
    const seen = () => {
      if (document.visibilityState === 'visible') void refetchToday();
    };
    document.addEventListener('visibilitychange', seen);
    window.addEventListener('pageshow', seen);
    return () => {
      document.removeEventListener('visibilitychange', seen);
      window.removeEventListener('pageshow', seen);
    };
  }, [refetchToday]);
  const clock = useNow();
  const myEmail = me.principal?.userDetails ?? null;
  // 18 Oct 2026: the Call item under a row's More is a dial link like CallButton's (the person's own way of calling)
  const callLink = useCallLink();

  // 1.6.1: a record that was moved or called off is history (the tenant's History, Look up), not a line on the day
  // 1 Nov 2026 (D-1102-CREW-3): what this phone is holding to send counts as done on this phone — a start waiting to
  // send is an arrival (Finish comes next), a Finish waiting to send has closed its visit (the next one may start)
  const offline = useOfflineQueue();
  const held = offline.items;
  const live = useMemo(() => {
    const waitingFinish = new Set(held.filter((a) => a.status === 'waiting' && a.kind === 'finish').map((a) => a.appointmentId));
    return (q.data?.visits ?? [])
      .filter((v) => isLiveOnDay(v.status))
      .map((v) => {
        let out = v;
        const start = held.find((a) => a.status === 'waiting' && a.kind === 'arrived' && a.appointmentId === v.appointmentId && startsVisit(a.body.answer as never));
        if (start && (!v.arrival || v.arrival.answer === 'noAnswer'))
          out = { ...out, arrival: { answer: start.body.answer as never, reason: null, next: null, movedTo: null, dayKey: start.day, at: new Date(start.createdAt).toISOString(), by: start.user } };
        if (out.closeOutFirst && waitingFinish.has(out.closeOutFirst.appointmentId)) out = { ...out, closeOutFirst: null };
        return out;
      });
  }, [q.data, held]);
  // 19/20 Oct 2026 (D-1019-ATTN): the Housekeeping Day is Housekeeping only, for every role — Tech Support only shared
  // the booking system. Tech visits live on Tech Support (/tech) and the house Today. The crew's list is unchanged
  // (a Classico crew has no Tech visits).
  const visits = useMemo(() => (isCrew ? live : live.filter((v) => !isTechVisit(v))), [live, isCrew]);
  const slot = q.data?.slot ?? null;
  // 1 Nov 2026 (D-1102-CREW-2): Today and the day plan under it read again every minute while this page is showing, so a
  // move the office makes is on the phone within a minute; a card whose time or order changed is marked for a few seconds
  const liveRefresh = liveRefreshOn(dayPlan.data?.settings);
  const refetchDayPlan = dayPlan.refetch;
  useVisibleInterval(
    () => {
      void refetchToday({ cancelRefetch: false });
      void refetchDayPlan({ cancelRefetch: false });
    },
    liveRefresh,
    LIVE_REFRESH_MS,
  );
  const before = useRef<TodayVisit[] | null>(null);
  const markTimer = useRef<number | undefined>(undefined);
  const [changed, setChanged] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (fromCopy || !q0.data) return;
    const was = before.current;
    before.current = visits;
    if (!was || !liveRefresh) return;
    const { ids, count } = changedBetween(was, visits);
    if (!count) return;
    setChanged(new Set(ids));
    announce(t('crew1102.changedCount', { count }));
    window.clearTimeout(markTimer.current);
    markTimer.current = window.setTimeout(() => setChanged(new Set()), CHANGED_MARK_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits, fromCopy]);
  useEffect(() => () => window.clearTimeout(markTimer.current), []);
  // with "The crew can save while offline" on, the Finish sheet's questions are read while there is a signal
  const qc = useQueryClient();
  const keepOffline = offline.enabled;
  useEffect(() => {
    if (!keepOffline || !isCrew || fromCopy || !q0.data) return;
    for (const v of visits) if (OPEN_STATUSES.has(v.status) && takesCompletionRecord(v.kind, v.completionRequired) && !v.notYours && !v.askOffice) prefetchFinishConfig(qc, v.appointmentId);
  }, [keepOffline, isCrew, fromCopy, q0.data, visits, qc]);
  // 1.6.6: two crews today — the lanes, and on the crew's phone which visits are not theirs
  const crews = q.data?.crews ?? null;
  const lanes = crews?.spots ?? null;
  const myLane = isCrew ? (crews?.mine ?? null) : null;
  const lockedFor = (v: TodayVisit) => isCrew && (!!v.notYours || !!v.askOffice);
  // 1.5.15: what is with the scheduler for a visit — the server's list, plus the press this device has just made.
  // A close-out request still open on a visit that is already closed adds nothing to its card.
  // 1 Nov 2026 (D-1102-CREW-3): and what is waiting on this phone for a signal holds the card's buttons back the same way
  const device = useMemo(
    () => [
      ...devicePresses(txs.tracked),
      ...held
        .filter((a) => a.status === 'waiting' && (a.kind === 'finish' || !startsVisit(a.body.answer as never)))
        .map((a) => ({ operation: a.kind === 'finish' ? 'Record Completion' : 'Start Service', transactionId: `queued-${a.id}`, appointmentId: a.appointmentId, open: true, submittedAt: new Date(a.createdAt).toISOString(), trackedAt: Number.MAX_SAFE_INTEGER })),
    ],
    [txs.tracked, held],
  );
  const pressesOf = (v: TodayVisit) => openPresses(v.appointmentId, v.pending, device, q.dataUpdatedAt, Date.now()).filter((p) => p.kind !== 'finish' || OPEN_STATUSES.has(v.status));
  const closingOf = (v: TodayVisit) => closingOut(pressesOf(v));

  const placed = visits.filter((v) => v.position !== null && ON_POSITION.has(v.status));
  const capacity = slot?.baseCapacity || 5;
  const hasSurge = !!slot?.surgeApproved || placed.some((v) => v.surge || (v.position ?? 0) > capacity);
  const positionCount = Math.max(capacity + (hasSurge ? 1 : 0), ...placed.map((v) => v.position ?? 0));
  // 1.5.0: each visit's own length lays out the day; once the crew has started, the later visits' expected times
  const plan = useDayLayout(day, visits, positionCount, true, lanes);
  const laidOf = (pos: number | null) => (pos ? plan.find((x) => x.position === pos) : undefined);
  const timeOf = (pos: number | null) => {
    const p = laidOf(pos);
    return p ? formatClock(p.startMinutes) : null;
  };
  /** "9:30–11:00" — the visit's span on the plan. */
  const spanOf = (pos: number | null) => {
    const p = laidOf(pos);
    return p ? `${formatClock(p.startMinutes)}–${formatClock(p.endMinutes)}` : null;
  };
  /** Today, once the crew has started: when a later visit is now expected (shown only when it differs). */
  const expectedOf = (v: TodayVisit) => {
    const p = laidOf(v.position);
    if (!p || p.expectedStart === null || v.status !== 'Scheduled' || Math.abs(p.expectedStart - p.startMinutes) < 5) return null;
    return formatClock(p.expectedStart);
  };
  // 1.7.8: the bent day — each card's window, the overlaps the office kept, the time given back, the day's end
  const bend = useDayBend(day, visits, plan);
  const lang = i18n.language.startsWith('es') ? 'es' : 'en';
  /** The minute of the day (service clock) of a stored instant, as the card says it: "2:50". */
  const clockOfInstant = (iso: string) => formatClock(serviceMinutesNow(new Date(iso)));
  const byPosition = new Map(placed.map((v) => [v.position!, v]));
  const facility = visits.filter((v) => v.kind === 'facility');
  const unplaced = visits.filter((v) => v.kind !== 'facility' && !placed.includes(v));
  const crewNeeded = slot ? Math.max(slot.crewSize, hasSurge && placed.length > capacity ? 4 : 0) : null;

  /** 1.6.8: a staff unit or an office — Start and End, times only (no door, no photos, no close-out) */
  const isStaffKind = (v: TodayVisit) => v.kind === 'staff' || v.kind === 'facility';
  /** Start was pressed today and the visit has not ended: the scheduler is asked nothing, so its status stays Scheduled */
  const staffStarted = (v: TodayVisit) => isStaffKind(v) && startsVisit(v.arrival?.answer) && v.arrival?.dayKey === day && v.status === 'Scheduled';
  const now = q.data ? visits.find((v) => (v.status === 'In Progress' || staffStarted(v)) && !lockedFor(v)) ?? null : null;
  /** Later on the same clock: a higher position — on a two-crew day, later in the same lane. */
  const laterThan = (v: TodayVisit, than: TodayVisit) =>
    lanes && v.position && than.position
      ? lanes[v.position]?.crew === lanes[than.position]?.crew && (lanes[v.position]?.n ?? 0) > (lanes[than.position]?.n ?? 0)
      : (v.position ?? 0) > (than.position ?? 0);
  const minutesNow = serviceMinutesNow();
  // 1.5.22: "late" — a tenant's visit still open the close-out reminder's minutes after its scheduled end (the same
  // fact the server lists as "Pending close-out"); how many minutes past its end, or null
  const lateAfter = closeoutLateAfter(dayPlan.data?.settings?.[CLOSEOUT_LATE_SETTING]);
  /** 1.6.3: the kind first, the record's flag second — a staff unit or a facility space is marked completed, never Arrived / Finish */
  const closesOut = (v: TodayVisit) => takesCompletionRecord(v.kind, v.completionRequired);
  const lateOf = (v: TodayVisit) => (closesOut(v) ? closeoutLate(laidOf(v.position)?.endMinutes, minutesNow, lateAfter) : null);
  // 25 Oct 2026 (D-1025-TODAY): the rule is shared with the house Today (shared/closeout-rule.ts) so both count the same visits.
  // The visit that is happening now has its own card — until it is late: then it is on this list too, because this is the one
  // list of late close-outs (Needs your attention no longer repeats them)
  const pendingCloseOut = placed.filter((v) => dayAwaitsCloseout({ status: v.status, closing: !!closingOf(v), locked: lockedFor(v), startMinutes: plan.find((x) => x.position === v.position)?.startMinutes ?? null, isNow: v === now, late: lateOf(v) !== null }, minutesNow));
  const lateCount = pendingCloseOut.filter((v) => lateOf(v) !== null).length;
  /** The visits this card lists: Needs your attention does not say them a second time. */
  const listedToClose = new Set(pendingCloseOut.map((v) => v.appointmentId));
  const startOf = (v: TodayVisit) => bend.windowOf(v.appointmentId)?.start ?? laidOf(v.position)?.startMinutes ?? 0;
  const canBeNext = (v: TodayVisit) => (v.status === 'Scheduled' || v.status === 'On Hold') && !lockedFor(v) && !pendingCloseOut.includes(v) && !closingOf(v);
  const sameLaneAs = (v: TodayVisit, than: TodayVisit) => !lanes || !v.position || !than.position || lanes[v.position]?.crew === lanes[than.position]?.crew;
  // 1.7.8: on a bent day "Next" is the next by start time (a card moved to 3:30 is not next because of its position)
  const next = bend.bent
    ? ([...placed].sort((a, b) => startOf(a) - startOf(b) || (a.position ?? 0) - (b.position ?? 0)).find((v) => v !== now && canBeNext(v) && !staffStarted(v) && (!now || (sameLaneAs(v, now) && startOf(v) >= startOf(now)))) ?? null)
    : (placed.find((v) => canBeNext(v) && (!now || laterThan(v, now))) ?? null);

  // Arrived (1.4.0): the crew's step before Finish on today's visits. 1.5.16: after "not home — try again later
  // today" the visit still stands, so Arrived is there for the next try.
  const [arrivedFor, setArrivedFor] = useState<TodayVisit | null>(null);
  const arrivedOpener = useRef<HTMLElement | null>(null);
  const canArrive = (v: TodayVisit) => finish.canFinish && v.status === 'Scheduled' && v.serviceDateKey === day && closesOut(v) && (!v.arrival || v.arrival.answer === 'noAnswer') && !v.closeOutFirst && !lockedFor(v) && !pressesOf(v).length;
  const arrivedButton = (v: TodayVisit, variant: 'default' | 'secondary' = 'default') =>
    canArrive(v) ? (
      <Button
        size="sm"
        variant={variant === 'default' ? undefined : 'secondary'}
        aria-label={t('arrived.buttonFor', { unit: v.unit })}
        onClick={(e) => {
          arrivedOpener.current = e.currentTarget;
          setArrivedFor(v);
        }}
        data-testid={`arrived-${v.appointmentId}`}
      >
        {t('arrived.button')}
      </Button>
    ) : null;
  // 1.7.8 (D-1.7.8-2): Move — another time today, for the roles that book; never the crew, never a finished card
  const [moveFor, setMoveFor] = useState<TodayVisit | null>(null);
  const moveOpener = useRef<HTMLElement | null>(null);
  const canMove = (v: TodayVisit) =>
    booking.canBook && !isCrew && v.serviceDateKey === day && MOVABLE.has(v.status) && v.kind !== 'facility' && v.position !== null && !v.closeout && !lockedFor(v) && !pressesOf(v).length;
  const laneOfVisit = (v: TodayVisit): BendLane => bend.windowOf(v.appointmentId)?.lane ?? (lanes && v.position ? (lanes[v.position]?.crew ?? null) : null);
  /**
   * 1.7.8 — the card's time column, read at a glance: "planned 3:30" (the crew sees it too), "planned 3:30 · started
   * 2:30", "recorded 2:50 by Elijah" / "started 2:30 · recorded 2:50", "moved · was 3:00" / "pushed by 1104 · was
   * 3:00", "overlaps 1104 by 20 min" (an icon and words). Nothing at all on a card nobody bent.
   */
  const bendLines = (v: TodayVisit) => {
    const c = v.chosen ?? null;
    const id = v.appointmentId;
    const state = bendStateOf(v);
    const a = v.arrival && v.arrival.dayKey === day && startsVisit(v.arrival.answer) ? v.arrival : null;
    const out: ReactNode[] = [];
    const startedAt = a ? serviceMinutesNow(new Date(a.at)) : null;
    if (c && state === 'planned' && c.start > minutesNow)
      out.push(
        <small key="planned" className="bend-line" data-testid={`planned-${id}`}>
          {t('bend178.planned', { time: formatClock(c.start) })}
        </small>,
      );
    else if (c && state === 'started' && startedAt !== null && startedAt !== c.start)
      out.push(
        <small key="started" className="bend-line" data-testid={`started-${id}`}>
          {t('bend178.plannedStarted', { planned: formatClock(c.start), started: formatClock(startedAt) })}
        </small>,
      );
    if (isCrew) return out;
    if (a?.recordedAt && startedAt !== null)
      out.push(
        <small key="recorded" className="bend-line" data-testid={`recorded-${id}`}>
          {t('bend178.startedRecorded', { started: formatClock(startedAt), time: clockOfInstant(a.recordedAt) })}
        </small>,
      );
    // a booking written down after the fact; a push is not something the pushed visit "recorded" (it says who pushed it)
    else if (c && !c.pushedBy && serviceMinutesNow(new Date(c.at)) > c.start)
      out.push(
        <small key="recorded" className="bend-line" data-testid={`recorded-${id}`}>
          {t('bend178.recordedBy', { time: clockOfInstant(c.at), name: pressName(c.by) })}
        </small>,
      );
    if (c && c.was !== null && c.was !== undefined && c.was !== c.start)
      out.push(
        <small key="moved" className="bend-line" data-testid={`moved-${id}`}>
          {c.pushedBy ? t('bend178.pushedBy', { unit: c.pushedBy, time: formatClock(c.was) }) : t('bend178.movedWas', { time: formatClock(c.was) })}
        </small>,
      );
    const over = bend.overlaps.get(id) ?? [];
    if (over.length)
      out.push(
        <small key="overlap" className="bend-line is-overlap" data-testid={`overlap-${id}`}>
          <Icon name="alert" aria-hidden="true" /> {over.map((o) => t('bend178.overlaps', { unit: o.unit, n: o.by })).join(' · ')}
        </small>,
      );
    return out;
  };
  /** A started card with a chosen start says both times itself, so "expected" is not said a second time. */
  const saysStarted = (v: TodayVisit) => !!v.chosen && bendStateOf(v) === 'started';
  const entryTag = (v: TodayVisit) => {
    const s = v.entryPermission?.status;
    // 1.5.16: a permission on file has its own line above the buttons (entryLine)
    if (!s || s === 'granted') return null;
    return <span data-testid={`entry-${v.appointmentId}`}>{s === 'asked' ? t('today.entryAsked') : t('today.needsHome')}</span>;
  };
  /**
   * 1.5.16: "Permission to enter: received Oct 5 by email" — above the buttons, so the crew sees it before Arrived.
   * 1.5.17: and "Permission to enter: not on file" when there is none (asked, or answered No, is none) — on a
   * tenant's housekeeping visit that is Scheduled today; not a staff unit, a facility space, a Tech ticket or a
   * visit that is under way or over.
   */
  const entryLine = (v: TodayVisit) =>
    v.entryPermission?.status === 'granted' ? (
      <p className="help entry-line" data-testid={`entry-line-${v.appointmentId}`}>
        <Icon name="check" /> <PermissionWords permission={v.entryPermission} />
      </p>
    ) : v.kind === 'tenant' && v.program === 'HOUSEKEEPING' && v.status === 'Scheduled' && v.serviceDateKey === day ? (
      <p className="help entry-line" data-testid={`entry-none-${v.appointmentId}`}>
        <Icon name="lock" /> {t('door17.none')}
      </p>
    ) : null;
  /** 1.5.16: after "not home — try again later today": the office has it; Arrived is still there for the next try. */
  const notHomeLine = (v: TodayVisit) =>
    v.arrival?.answer === 'noAnswer' && v.status === 'Scheduled' && v.arrival.dayKey === day ? (
      <p className="help" data-testid={`not-home-${v.appointmentId}`}>
        <Icon name="info" /> {t('door16.notHomeLine', { time: formatClockOf(v.arrival.at, i18n.language) })}
      </p>
    ) : null;

  // The close-out reminder's "Close out now" (1.5.0): /?closeout=<AppointmentID> opens that visit's Finish sheet.
  const [params, setParams] = useSearchParams();
  const wantCloseout = params.get('closeout');
  useEffect(() => {
    if (!wantCloseout || !q.data || fromCopy) return;
    const v = q.data.visits.find((x) => x.appointmentId === wantCloseout);
    if (v && closingOf(v)) announce(t('tx.closingOut'));
    else if (v && finish.canFinish && closesOut(v)) finish.openFinish({ appointmentId: v.appointmentId, unit: v.unit, tenantName: v.tenantName, serviceDateKey: v.serviceDateKey, time: timeOf(v.position) }, null);
    else if (!v) announce(t('today.closeoutNotToday'));
    params.delete('closeout');
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantCloseout, q.data]);

  // The typed address /book (1.5.13): /?book=1 opens the Book sheet, for the roles that book.
  const wantBook = params.get('book');
  useEffect(() => {
    if (!wantBook) return;
    if (booking.canBook) booking.openBook({}, null);
    params.delete('book');
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantBook]);

  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (!q.data || fromCopy || announced.current === day) return;
    announced.current = day;
    const n = q.data.visits.filter((v) => v.status !== 'Cancelled' && v.status !== 'Rescheduled' && (isCrew || !isTechVisit(v))).length;
    announce(n ? t('today.loaded', { count: n }) : t('today.loadedNone'));
  }, [q.data, fromCopy, day, announce, t, isCrew]);

  const openFinish = (v: TodayVisit, el: HTMLElement, mode: 'record' | 'edit' | 'photos' = 'record') =>
    finish.openFinish({ appointmentId: v.appointmentId, unit: v.unit, tenantName: v.tenantName, serviceDateKey: v.serviceDateKey, time: timeOf(v.position) }, el, { mode });
  /** 1.6.5 (D-1.6.5-5): arrived and still open past the planned length plus the setting — for the office's list. */
  const openLong = visits.flatMap((v) => {
    const m = OPEN_STATUSES.has(v.status) && !v.closeout && v.arrival?.dayKey === day ? openTooLong(v.arrival, v.lengthMinutes ?? 60, openTooLongMinutes(dayPlan.data?.settings), clock) : null;
    return m === null ? [] : [{ appointmentId: v.appointmentId, unit: v.unit, minutes: m }];
  });
  /** 1.6.5: "Close out 703 first." — the way to 703's close-out from the card that is waiting for it. */
  // 1.7.8: a start the office chose that has passed is offered first on Start ("Booked for 2:30")
  const target = (v: TodayVisit) => ({ appointmentId: v.appointmentId, unit: v.unit, tenantName: v.tenantName, serviceDateKey: v.serviceDateKey, ...(v.chosen ? { chosenStart: v.chosen.start } : {}) });
  const openCloseOutOf = (appointmentId: string, el: HTMLElement) => {
    const first = visits.find((x) => x.appointmentId === appointmentId);
    if (!first) return;
    // 1.6.8: a staff unit that was started is ended, not closed out
    if (closesOut(first)) openFinish(first, el);
    else void finish.markCompleted(target(first));
  };
  const firstIsStaff = (v: TodayVisit) => {
    const first = v.closeOutFirst ? visits.find((x) => x.appointmentId === v.closeOutFirst!.appointmentId) : null;
    return !!first && !closesOut(first);
  };
  /**
   * The close-out press a visit takes now: Finish (a completion record) or — 1.6.8 — End (a staff unit or an office:
   * the record is marked completed with the time and who; what "Mark completed" was).
   */
  const closeButton = (v: TodayVisit, variant: 'default' | 'secondary' = 'default', testid = `finish-${v.appointmentId}`) =>
    !finish.canFinish ? null : closesOut(v) ? (
      <Button size="sm" variant={variant === 'default' ? undefined : 'secondary'} onClick={(e) => openFinish(v, e.currentTarget)} data-testid={testid}>
        {t('today.finish')}
      </Button>
    ) : (
      <Button size="sm" variant={variant === 'default' ? undefined : 'secondary'} aria-label={t('card168.endFor', { unit: v.unit })} onClick={() => finish.markCompleted(target(v))} data-testid={`mark-${v.appointmentId}`}>
        {t('card168.end')}
      </Button>
    );
  /** 1.6.8: Start — today, not started, nothing with the scheduler, and (the crew) no earlier visit of theirs still open */
  const canStart = (v: TodayVisit) => finish.canFinish && isStaffKind(v) && v.status === 'Scheduled' && v.serviceDateKey === day && !v.arrival && !v.closeOutFirst && !lockedFor(v) && !pressesOf(v).length;
  const startButton = (v: TodayVisit, testid = `start-${v.appointmentId}`, variant: 'default' | 'secondary' = 'default') => (
    <Button size="sm" variant={variant === 'default' ? undefined : 'secondary'} aria-label={t('card168.startFor', { unit: v.unit })} onClick={() => finish.startStaff(target(v))} data-testid={testid}>
      {t('card168.start')}
    </Button>
  );

  // 1.8.3 (D-1.8.3-1): Today prints no email and no phone number, for every role — the crew press Call, and the
  // office has both in Look up and on the person's card. "Phone numbers on Today" no longer changes this page.
  // 1.5.4: the tenant's contact person with "Call them first" — named (never their number) above the tenant's own Call
  const callFirstLine = (v: TodayVisit) =>
    v.callFirst ? (
      <span className="call-first" data-testid={`call-first-${v.appointmentId}`}>
        <Icon name="phone" aria-hidden="true" /> {t('today.callFirst', { name: v.callFirst.name, rel: v.callFirst.relationship ? t(`people.contactPerson.rel.${v.callFirst.relationship}`) : t('people.contactPerson.title') })}
      </span>
    ) : null;

  const row = (v: TodayVisit, pos: number | null) => {
    const status = appointmentStatusKey(v.status, v.confirmation);
    const done = ['completed', 'cancelled', 'noShow', 'unable', 'rescheduled'].includes(status);
    const time = timeOf(pos);
    // with the scheduler: the line stands where the buttons were — no second press
    const presses = pressesOf(v);
    const closing = closingOut(presses);
    const waiting = closing ?? presses[0] ?? null;
    // 1.5.16: once someone has arrived (tenant home, or entered with permission) the visit's press is Finish
    const arrivedIn = startsVisit(v.arrival?.answer) && v.arrival?.dayKey === day && v.status === 'Scheduled' && closesOut(v);
    const canFinish = !waiting && (v.status === 'In Progress' || pendingCloseOut.includes(v) || arrivedIn || (!closesOut(v) && v.status === 'Scheduled' && v.serviceDateKey <= day));
    const co = v.closeout;
    // 1.6.8 (D-1.6.8-2): the card carries Call, the visit's own press and ONE secondary button — for every role.
    // The press: Arrived until someone has arrived, then Finish — never both (a late close-out with no arrival has
    // its Finish on the "visits to close out" list above); Start, then End, on a staff unit or an office.
    const staffKind = isStaffKind(v);
    const startedStaff = staffStarted(v);
    const showStart = canStart(v);
    const showFinish = canFinish && !canArrive(v) && !showStart && (!staffKind || startedStaff || v.status === 'In Progress' || v.serviceDateKey < day);
    // "after": closed out, marked completed, or over in any other way — Review visit takes the place of Office action
    const after = !!co || !BEFORE_OR_DURING.has(v.status);
    const reviewable = canReviewVisit(me.role) && !v.source && v.program !== 'TECH';
    // 1.6.8 (D-1.6.8-1): a Tech Support visit whose agreement is not signed cannot start
    const noAgreement = !!v.agreementMissing;
    // Eli, 13 Oct 2026: a paused tenant "cannot go" — said on the card, and nothing to press but Call
    const paused = !!v.paused;
    const blocked = noAgreement || paused;
    // the profile behind the name: People for those who have it, Look up for the schedule's other roles
    const profile = v.kind === 'facility' || isCrew || !v.tenantId ? null : staff ? `/people/${encodeURIComponent(v.tenantId)}` : `/lookup?t=${encodeURIComponent(v.tenantId)}`;
    const last = txs.lastFor(v.appointmentId);
    const refused = v.closeoutRefused && last?.transactionId !== v.closeoutRefused.transactionId ? v.closeoutRefused : null;
    // 1.6.6: the other crew's visit on the crew's phone — shown, and nothing to press
    const locked = lockedFor(v);
    /**
     * 18 Oct 2026 (D-1018-HK): the office's row shows ONE likely button and a "More" menu with the rest; the crew's row
     * is unchanged below (Call and their one press). The rule is ./today/primaryAction.ts; every action the row had
     * is still here — under More when it is not the likely one.
     */
    const officeAllowed = v.status !== 'Proposed' && !(after && reviewable) && staff && !waiting && (after ? v.kind !== 'facility' || (me.role === 'admin' && REOPENABLE.includes(v.status)) : v.kind !== 'facility' || v.status === 'Scheduled');
    const facts: RowFacts = {
      status: v.status,
      waiting: !!waiting,
      isNext: v === next,
      unconfirmed: v.kind === 'tenant' && v.confirmation !== 'Confirmed',
      closesOut: closesOut(v),
      showFinish: showFinish && finish.canFinish,
      showStart,
      canArrive: canArrive(v),
      after,
      // Review visit in the row is the office's and the scheduler's (a viewer keeps it beside the name)
      reviewable: after && reviewable && REVIEW_ROW_ROLES.includes(me.role!),
      officeAllowed,
      canBook: booking.canBook,
      canMove: canMove(v),
      hasPhone: !!(v.callFirst?.phone || v.phone),
    };
    const officeActions = () => {
      const available = availableActions(facts);
      let primary = primaryActionFor(facts);
      // nothing likely and one thing possible (Call): that one is the button, with no menu over one item
      if (primary === 'none' && available.size === 1) primary = [...available][0]!;
      const more = moreActionsFor(primary, available);
      const callPhone = v.callFirst?.phone ?? v.phone ?? null;
      const callName = v.callFirst?.name ?? v.tenantName;
      const openMove = (el: HTMLElement) => {
        moveOpener.current = el;
        setMoveFor(v);
      };
      const openArrived = (el: HTMLElement) => {
        arrivedOpener.current = el;
        setArrivedFor(v);
      };
      /** The likely button, as the row showed it before (the same words, the same test ids). */
      const primaryButton = (): ReactNode => {
        if (blocked && (primary === 'arrived' || primary === 'finish' || primary === 'end' || primary === 'start'))
          return (
            <Button size="sm" disabled aria-describedby={`no-agreement-${v.appointmentId}`} data-testid={`start-off-${v.appointmentId}`}>
              {closesOut(v) && canArrive(v) ? t('arrived.button') : closesOut(v) ? t('today.finish') : t('card168.end')}
            </Button>
          );
        switch (primary) {
          case 'call':
            return <CallButton phone={callPhone} name={callName} />;
          case 'arrived':
            return arrivedButton(v);
          case 'start':
            return startButton(v);
          case 'finish':
          case 'end':
            return closeButton(v);
          case 'answer':
            return (
              <Button size="sm" aria-label={`${t('attention.recordAnswer')} · ${v.unit}`} onClick={(e) => booking.openActions(v, 'answer', e.currentTarget)} data-testid={`answer-${v.appointmentId}`}>
                {t('attention.recordAnswer')}
              </Button>
            );
          case 'move':
            return (
              <Button size="sm" variant="secondary" className="move-day" aria-label={t('bend178.moveFor', { unit: v.unit })} onClick={(e) => openMove(e.currentTarget)} data-testid={`move-day-${v.appointmentId}`}>
                {t('bend178.move')}
              </Button>
            );
          case 'review':
            return <ReviewVisitLink role={me.role} visit={v} place="row" />;
          case 'office':
            return (
              <Button size="sm" variant="secondary" aria-label={t('office17.buttonFor', { unit: v.unit })} onClick={(e) => booking.openActions(v, 'menu', e.currentTarget, { office: true })} data-testid={`actions-${v.appointmentId}`}>
                {t('office17.button')}
              </Button>
            );
          case 'waitingConfirm':
            return (
              <Button size="sm" variant="secondary" aria-label={t('today.waitingConfirmFor', { unit: v.unit })} onClick={(e) => booking.openActions(v, 'menu', e.currentTarget, { office: false })} data-testid={`actions-${v.appointmentId}`}>
                {t('today.waitingConfirm')}
              </Button>
            );
          default:
            return null;
        }
      };
      /** The same action as a line of the More menu (the same words and test ids, so nothing is lost). */
      const item = (a: RowPrimary): MenuItem | null => {
        const id = v.appointmentId;
        switch (a) {
          case 'call':
            return callPhone ? { kind: 'anchor', id: 'call', label: t('contact.call'), anchor: { ...callLink(callPhone, t('contact.callName', { name: callName })), className: 'menu-item call' }, testid: `more-call-${id}` } : null;
          case 'arrived':
            return blocked ? null : { kind: 'button', id: 'arrived', label: t('arrived.button'), ariaLabel: t('arrived.buttonFor', { unit: v.unit }), onSelect: openArrived, testid: `arrived-${id}` };
          case 'start':
            return blocked ? null : { kind: 'button', id: 'start', label: t('card168.start'), ariaLabel: t('card168.startFor', { unit: v.unit }), onSelect: () => void finish.startStaff(target(v)), testid: `start-${id}` };
          case 'finish':
            return blocked || !finish.canFinish ? null : { kind: 'button', id: 'finish', label: t('today.finish'), onSelect: (el) => openFinish(v, el), testid: `finish-${id}` };
          case 'end':
            return blocked || !finish.canFinish ? null : { kind: 'button', id: 'end', label: t('card168.end'), ariaLabel: t('card168.endFor', { unit: v.unit }), onSelect: () => void finish.markCompleted(target(v)), testid: `mark-${id}` };
          case 'answer':
            return { kind: 'button', id: 'answer', label: t('attention.recordAnswer'), onSelect: (el) => booking.openActions(v, 'answer', el), testid: `answer-${id}` };
          case 'move':
            return { kind: 'button', id: 'move', label: t('bend178.move'), ariaLabel: t('bend178.moveFor', { unit: v.unit }), onSelect: openMove, testid: `move-day-${id}` };
          case 'review':
            return { kind: 'link', id: 'review', label: t('review165.button'), ariaLabel: t('review165.buttonFor', { unit: v.unit }), to: reviewPath(id), testid: `review-${id}` };
          case 'office':
            return { kind: 'button', id: 'office', label: t('office17.button'), ariaLabel: t('office17.buttonFor', { unit: v.unit }), onSelect: (el) => booking.openActions(v, 'menu', el, { office: true }), testid: `actions-${id}` };
          case 'waitingConfirm':
            return { kind: 'button', id: 'waiting', label: t('today.waitingConfirm'), ariaLabel: t('today.waitingConfirmFor', { unit: v.unit }), onSelect: (el) => booking.openActions(v, 'menu', el, { office: false }), testid: `actions-${id}` };
          default:
            return null;
        }
      };
      const items = more.map(item).filter((x): x is MenuItem => !!x);
      return (
        <div className="row-actions">
          {primaryButton()}
          {items.length ? <MenuButton label={t('today.more')} ariaLabel={t('today.moreFor', { unit: v.unit })} items={items} testid={`more-${v.appointmentId}`} /> : null}
        </div>
      );
    };
    return (
      <li key={v.id} className={`visit${done ? ' is-done' : ''}${locked ? ' is-locked' : ''}${paused ? ' is-paused' : ''}${changed.has(v.appointmentId) ? ' is-changed-1102' : ''}`} data-changed={changed.has(v.appointmentId) ? 'true' : undefined} data-testid={pos ? `position-${pos}` : `visit-${v.appointmentId}`} data-status={status} data-locked={locked ? 'true' : undefined}>
        <div className="time tnum">
          {/* 14 Oct 2026 (Eli): tap the time to change it — the same Move sheet, one press instead of the arrows */}
          {pos && canMove(v) ? (
            <button
              type="button"
              className="time-tap"
              aria-label={t('bend178.tapTime', { unit: v.unit, time: time ?? '' })}
              onClick={(e) => {
                moveOpener.current = e.currentTarget;
                setMoveFor(v);
              }}
              data-testid={`time-tap-${v.appointmentId}`}
            >
              {time ?? '—'}
            </button>
          ) : (
            (time ?? '—')
          )}
          {pos && v.lengthMinutes && v.kind !== 'facility' ? <small data-testid={`span-${v.appointmentId}`}>{t('today.span', { span: spanOf(pos) ?? '', len: lengthWords(laidOf(pos)?.minutes ?? v.lengthMinutes) })}</small> : null}
          {/* 1.6.4: a space takes no position and no span, but its planned length shows like every visit's */}
          {v.kind === 'facility' && v.lengthMinutes ? <small data-testid={`span-${v.appointmentId}`}>{lengthWords(v.lengthMinutes)}</small> : null}
          {bendLines(v)}
          {expectedOf(v) && !saysStarted(v) ? <small className="expected" data-testid={`expected-${v.appointmentId}`}>{t('today.expected', { time: expectedOf(v) })}</small> : null}
          {v.timeLabel ? <small data-testid={`time-label-${v.appointmentId}`}>{t('arrived.timeLabel', { label: v.timeLabel })}</small> : null}
          <small>
            {pos ? t('today.positionN', { n: laidOf(pos)?.laneIndex ?? pos }) : ''}
            {pos && v.servicePeriod ? ' · ' : ''}
            {t(`period.${periodAt(laidOf(pos)?.startMinutes) ?? v.servicePeriod}`, { defaultValue: v.servicePeriod })}
          </small>
          {/* 1.7.8: Move sat here, under the window. 18 Oct 2026 (D-1018-HK): it is under the row's More now (the time
              itself is still a button that opens Move, Eli's 14 Oct press); the crew never had it */}
        </div>
        <div className="who">
          <div className="apt">
            {v.unit}{' '}
            <span>
              ·{' '}
              {profile ? (
                <Link className="name-link" to={profile} aria-label={t('card168.profileOf', { name: visitName(v) })} data-testid={`name-${v.appointmentId}`}>
                  {visitName(v)}
                </Link>
              ) : (
                visitName(v)
              )}
            </span>
          </div>
          <div className="sub">
            <span>{v.kind === 'facility' ? (v.locationDetails ?? t('status.facility')) : v.program === 'TECH' ? `${t('techBook.title')} · ${v.locationDetails ?? ''}` : t('programs.housekeeping')}</span>
            {v.cycleVisit ? (
              <span>{v.cycleVisit.of ? t('today.cycleVisit', { n: v.cycleVisit.n, of: v.cycleVisit.of }) : t('today.cycleVisitNoLimit', { n: v.cycleVisit.n })}</span>
            ) : null}
            {v.holdUntil ? <span>{t('today.holdUntil', { time: formatClockOf(v.holdUntil, i18n.language) })}</span> : null}
            {locked ? null : callFirstLine(v)}
            {/* 1.6.8: with a "Call them first" person the row's one Call is theirs; the tenant's own number is this link */}
            {!locked && v.callFirst && v.phone ? (
              <CallLink className="call-tenant" phone={v.phone} label={t('contact.callName', { name: v.tenantName })} data-testid={`call-tenant-${v.appointmentId}`}>
                {t('card168.callTenant', { name: v.preferredName ?? v.tenantName })}
              </CallLink>
            ) : null}
            {entryTag(v)}
          </div>
          {co && v.status === 'Completed' ? (
            <div className="closed-line" data-testid={`closed-${v.appointmentId}`}>
              {co.editedBy
                ? t('today.editedBy', { by: co.editedBy, time: formatClockOf(co.editedAt!, i18n.language) })
                : t('today.closedOutBy', { by: co.by, time: formatClockOf(co.at, i18n.language) })}
            </div>
          ) : null}
          {/* 1.6.5: Review visit — what happened to this visit, on one screen. 1.6.7: the crew has it too, read-only,
              here beside the name (their row of buttons stays Call and one button); the office's is in the row */}
          {/* 1.6.8: after the visit only — before and during there is nothing to review */}
          {locked || !after ? null : <ReviewVisitLink role={me.role} visit={v} place="aside" />}
        </div>
        <div className="side">
          <div className="badges">
            <StatusBadge status={status} />
            {visitTags(v).map((tag) => (
              <StatusBadge key={tag} status={tag} />
            ))}
            <CrewTag crew={v.crewTag} testid={`crew-tag-${v.appointmentId}`} />
            {changed.has(v.appointmentId) ? (
              <span className="status hold has-icon changed-tag-1102" data-testid={`changed-${v.appointmentId}`}>
                <Icon name="refresh" />
                {t('crew1102.changedTag')}
              </span>
            ) : null}
            {v.awaitingAgreement ? (
              <span className="status hold has-icon" data-testid={`awaiting-agreement-${v.appointmentId}`}>
                <Icon name="clock" />
                {t('agreement.awaiting')}
              </span>
            ) : null}
            {v.awaitingApproval ? (
              <span className="status hold has-icon" data-testid={`awaiting-${v.appointmentId}`}>
                <Icon name="clock" />
                {t('approval.badge')}
              </span>
            ) : null}
          </div>
          {offline.waitingFor(v.appointmentId).length ? (
            <span className="queue-line-1102" role="status" data-testid={`queued-line-${v.appointmentId}`}>
              <Icon name="clock" />
              <span>{t('crew1102.queueLine')}</span>
            </span>
          ) : closing ? (
            <WithScheduler closeout at={closing.at} testid={`closing-${v.appointmentId}`} />
          ) : waiting ? (
            <WithScheduler at={waiting.at} testid={`with-scheduler-${v.appointmentId}`} />
          ) : stalePress(v.pending, Date.now()) && OPEN_STATUSES.has(v.status) ? (
            <p className="help" role="status" data-testid={`stale-${v.appointmentId}`}>
              <Icon name="alert" /> {t('tx.stale', { time: formatClockOf(stalePress(v.pending, Date.now())!.at, i18n.language) })}
            </p>
          ) : refused ? (
            <OutcomeLine tx={refused} testid={`closeout-refused-${v.appointmentId}`} />
          ) : last ? (
            <OutcomeLine tx={last} />
          ) : null}
          {entryLine(v)}
          {notHomeLine(v)}
          <PressLines v={v} day={day} me={myEmail} canUndo={finish.canFinish && !locked && !offline.waitingFor(v.appointmentId).length} now={clock} onCloseOut={openCloseOutOf} firstIsStaff={firstIsStaff(v)} />
          {locked ? (
            <p className="help not-yours" data-testid={`not-yours-${v.appointmentId}`}>
              <Icon name="lock" /> {v.notYours ? t('crews166.notYours', { crew: v.notYours }) : t('crews166.askOffice')}
            </p>
          ) : null}
          {noAgreement ? (
            <p className="help no-agreement" id={`no-agreement-${v.appointmentId}`} data-testid={`no-agreement-${v.appointmentId}`}>
              <Icon name="lock" /> {t('tech168.notSigned')}
            </p>
          ) : paused ? (
            <p className="warn paused-line" id={`no-agreement-${v.appointmentId}`} role="note" data-testid={`paused-${v.appointmentId}`}>
              <Icon name="lock" /> <b>{t('today.pausedTitle')}</b> {t('today.pausedBody')}
            </p>
          ) : null}
          {locked ? null : !isCrew ? (
            officeActions()
          ) : (
            <div className="row-actions">
              {/* 1.6.8: one Call — the "Call them first" person when there is one (the tenant's own number is a link under the name) */}
              {v.callFirst ? <CallButton phone={v.callFirst.phone} name={v.callFirst.name} /> : <CallButton phone={v.phone} name={v.tenantName} />}
              {/* the visit's own press, for the crew and the office alike */}
              {blocked && (canArrive(v) || showFinish || showStart) ? (
                <Button size="sm" disabled aria-describedby={`no-agreement-${v.appointmentId}`} data-testid={`start-off-${v.appointmentId}`}>
                  {closesOut(v) && canArrive(v) ? t('arrived.button') : closesOut(v) ? t('today.finish') : t('card168.end')}
                </Button>
              ) : (
                <>
                  {arrivedButton(v)}
                  {showStart ? startButton(v) : null}
                  {showFinish ? closeButton(v) : null}
                </>
              )}
              {/* the ONE secondary button. 1.5.13: a proposal in pencil is waiting for the tenant's answer — say so; the
                  schedule's own press (the scheduler's too). Before and during the visit: "Office action" (1.5.17 — what
                  the tenant asked the office for by phone; an administrator's or a co-administrator's; 1.6.4: an office
                  still to be cleaned has it for Reschedule). After it: "Review visit", which holds Edit close-out, Send
                  update, Reopen and Move — the card has no second way to any of them. A visit Review does not cover (a
                  Tech Support ticket, a visit from before the app) keeps Office action after it, for Reopen. */}
              {v.status === 'Proposed' ? (
                booking.canBook && !waiting ? (
                  <Button size="sm" variant="secondary" aria-label={t('today.waitingConfirmFor', { unit: v.unit })} onClick={(e) => booking.openActions(v, 'menu', e.currentTarget, { office: false })} data-testid={`actions-${v.appointmentId}`}>
                    {t('today.waitingConfirm')}
                  </Button>
                ) : null
              ) : after && reviewable ? (
                <ReviewVisitLink role={me.role} visit={v} place="row" />
              ) : staff && !waiting && (after ? v.kind !== 'facility' || (me.role === 'admin' && REOPENABLE.includes(v.status)) : v.kind !== 'facility' || v.status === 'Scheduled') ? (
                <Button size="sm" variant="secondary" aria-label={t('office17.buttonFor', { unit: v.unit })} onClick={(e) => booking.openActions(v, 'menu', e.currentTarget, { office: true })} data-testid={`actions-${v.appointmentId}`}>
                  {t('office17.button')}
                </Button>
              ) : null}
              {/* Eli, 16 Oct 2026: a sent close-out is not changed by the crew — they add photos (the administrator corrects the original; the office adds photos from Review visit) */}
              {isCrew && co && finish.canFinish && (v.status === 'Completed' || v.status === 'In Progress') ? (
                <Button size="sm" variant="secondary" aria-label={t('today.addPhotosFor', { unit: v.unit })} onClick={(e) => openFinish(v, e.currentTarget, 'photos')} data-testid={`add-photos-${v.appointmentId}`}>
                  {t('today.addPhotos')}
                </Button>
              ) : null}
              {isCrew && co?.updatePending && finish.canFinish ? (
                <Button size="sm" aria-label={t('today.sendUpdateFor', { unit: v.unit })} onClick={() => finish.sendUpdate(co, v.unit)} data-testid={`send-update-${v.appointmentId}`}>
                  {t('today.sendUpdate')}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </li>
    );
  };

  const openRow = (pos: number) => (
    <li key={`open-${pos}`} className="visit is-open" data-testid={`position-${pos}`} data-status="open">
      <div className="time tnum">
        {timeOf(pos) ?? '—'}
        <small>{t('today.positionN', { n: laidOf(pos)?.laneIndex ?? pos })}</small>
      </div>
      <div className="who">
        <div className="apt">{t('today.open')}</div>
        <div className="sub">
          <span>{pos > capacity ? t('today.surgePosition') : t('today.openHelp')}</span>
        </div>
      </div>
      <div className="side">
        {pos > capacity ? <StatusBadge status="surge" /> : null}
        {booking.canBook ? (
          <div className="row-actions">
            <Button
              size="sm"
              variant="secondary"
              aria-label={t('today.bookPosition', { time: timeOf(pos) ?? '', n: pos })}
              onClick={(e) => booking.openBook({ dayKey: day }, e.currentTarget)}
              data-testid={`book-open-${pos}`}
            >
              <Icon name="plus" />
              {t('nav.book')}
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );

  /** 1.7.8: "Free 2:30–3:30 · Book something here" — the time given back, as a card at its time (an offer; nothing moves by itself). */
  const gapRow = (g: FreeGap) => (
    <li key={`gap-${g.lane ?? 'day'}-${g.from}`} className="free-gap" data-testid={`free-gap-${g.from}`}>
      <div className="time tnum">{formatClock(g.from)}</div>
      <div className="who">
        <div className="apt">
          {t('bend178.free', { from: formatClock(g.from), to: formatClock(g.to) })}
          {booking.canBook ? <span> · {t('bend178.freeHelp')}</span> : null}
        </div>
      </div>
      {booking.canBook ? (
        <div className="side">
          <Button size="sm" variant="secondary" aria-label={t('bend178.freeBookFor', { from: formatClock(g.from) })} onClick={(e) => booking.openBook({ dayKey: day, startMinutes: g.from }, e.currentTarget)} data-testid={`free-gap-book-${g.from}`}>
            <Icon name="plus" />
            {t('nav.book')}
          </Button>
        </div>
      ) : null}
    </li>
  );
  /**
   * 1.7.8 (D-1.7.8-0): one list of the day (or one lane of it). On an ordinary day the positions in the order given,
   * exactly as before; once a card in it has a chosen start, in start-time order (the window's start, then the
   * position; a free position by its own label). A free gap of the lane goes in at its time.
   */
  /**
   * 15 Oct 2026 (Eli): an empty position whose time a moved visit now fills is not "Open" — 615 moved to 10:30 left
   * position 2 (10:30) empty, and the card said 10:30 was free. The time is taken; real free time shows as a free gap.
   */
  const coveredByMove = (pos: number, lane: BendLane): boolean => {
    const slot = laidOf(pos);
    if (!slot) return false;
    // the time the row says is its label (timeOf): that is the time that must not read "Open" when a visit fills it
    return bend.windows.some((w) => (w.lane ?? null) === lane && w.start < slot.endMinutes && w.end > slot.startMinutes);
  };
  const listOf = (positions: readonly number[], lane: BendLane, o: { open: boolean; gaps: boolean }): ReactNode[] => {
    const items: { at: number; pos: number; node: ReactNode }[] = [];
    for (const pos of positions) {
      const v = byPosition.get(pos);
      if (v) items.push({ at: startOf(v), pos, node: row(v, pos) });
      else if (o.open && !coveredByMove(pos, lane)) items.push({ at: laidOf(pos)?.startMinutes ?? 0, pos, node: openRow(pos) });
    }
    if (bend.bent && bend.windows.some((w) => w.chosen && (w.lane ?? null) === lane)) items.sort((a, b) => a.at - b.at || a.pos - b.pos);
    if (o.gaps)
      for (const g of bend.gaps) {
        if ((g.lane ?? null) !== lane) continue;
        const i = items.findIndex((x) => x.at >= g.from);
        items.splice(i < 0 ? items.length : i, 0, { at: g.from, pos: 0, node: gapRow(g) });
      }
    return items.map((x) => x.node);
  };
  /** The day's end, after the last card — only when the day is bent or something ends past it. */
  const pastEnd = bend.windows.filter((w) => w.state !== 'finished' && w.end > bend.dayEnd);
  const dayEndLine =
    q.data && (bend.bent || pastEnd.length) ? (
      <p className="day-end" data-testid="day-end-line">
        <span className="day-end-words">
          <Icon name="clock" aria-hidden="true" /> {t('bend178.dayEnd', { time: clockWithPeriod(bend.dayEnd, lang) })}
        </span>
        {pastEnd.map((w) => (
          <span key={w.id} className="past-end" data-testid={`past-end-${w.id}`}>
            <Icon name="alert" aria-hidden="true" /> {t('bend178.pastEnd', { unit: w.unit, end: clockWithPeriod(w.end, lang), dayEnd: clockWithPeriod(bend.dayEnd, lang) })}
          </span>
        ))}
      </p>
    ) : null;

  // what the Move sheet is told: the card's window now, and the free gap of its lane (offered first)
  const movedWindow = moveFor ? bend.windowOf(moveFor.appointmentId) : null;
  const moveWindow = movedWindow ? { start: movedWindow.start, end: movedWindow.end } : null;
  const laneGap = moveFor ? bend.gaps.find((g) => (g.lane ?? null) === laneOfVisit(moveFor)) : undefined;
  const moveGap = laneGap ? { from: laneGap.from, to: laneGap.to } : null;

  const visitCount = visits.filter((v) => v.status !== 'Cancelled' && v.status !== 'Rescheduled' && v.kind !== 'facility' && !lockedFor(v)).length;
  // 1.6.6: the lists of a two-crew day — a lane in its own order; on the crew's phone, theirs and the other crew's
  const inLaneOrder = (list: TodayVisit[]) => (lanes ? [...list].sort((a, b) => (lanes[a.position!]?.crew ?? '').localeCompare(lanes[b.position!]?.crew ?? '') || (lanes[a.position!]?.n ?? 0) - (lanes[b.position!]?.n ?? 0)) : list);
  const mineFirst = inLaneOrder(placed.filter((v) => !v.notYours));
  const notMine = inLaneOrder(placed.filter((v) => !!v.notYours));
  const otherCrew = notMine[0]?.notYours ?? null;
  // The six-visit warning shows on Today for the day too (a warning, not a cap).
  // 1.6.6: with two crews the warning comes at the 7th visit
  const warnAt = lanes ? Math.max(bookingSetting(dayPlan.data?.settings, 'HK_WARN_AT'), TWO_CREW_WARN_AT) : bookingSetting(dayPlan.data?.settings, 'HK_WARN_AT');
  const sixToday = placed.filter((v) => v.status !== 'Cancelled' && v.status !== 'Rescheduled').length;
  const dayPending = txs.pendingForDay(day).filter((x) => !visits.some((v) => v.appointmentId === x.badgeAppointmentId));
  /** The day's visits to close out, one line each — the crew's card and the office's attention list show the same lines. */
  const closeoutRows = pendingCloseOut.map((v) => (
    <li key={v.id} data-testid={`pending-row-${v.appointmentId}`}>
      <span className="dot" style={{ background: 'var(--st-attn-dot)' }} aria-hidden="true" />
      <div className="t">
        <b>
          {v.unit} · {visitName(v)}
        </b>
        <span>{t('today.pendingSub', { time: timeOf(v.position) ?? '' })}</span>
      </div>
      {/* 1.5.22: past the reminder's minutes — said in words, with how late (never by colour alone) */}
      {lateOf(v) !== null ? (
        <span className="status attn has-icon late-tag" data-testid={`late-${v.appointmentId}`}>
          <Icon name="clock" />
          {t('today.lateTag', { n: lateOf(v) })}
        </span>
      ) : null}
      {/* the visit that is happening now is here only because it is late: its button has its own name (its card and its row keep theirs) */}
      {/* 1.6.8: a staff unit that has not been started is started first, here as on its card */}
      {canStart(v) ? startButton(v, `start-pending-${v.appointmentId}`, 'secondary') : closeButton(v, 'secondary', v === now ? `finish-late-${v.appointmentId}` : undefined)}
    </li>
  ));

  return (
    <section
      aria-labelledby="h-today"
      className={`${isCrew ? '' : 'hk1018'}${fromCopy ? ' today-stale-copy' : ''}`.trim() || undefined}
      aria-busy={fromCopy || undefined}
      data-stale-copy={fromCopy ? 'true' : undefined}
      {...(fromCopy ? staleGuards(() => announce(t(q0.isError ? 'crew1102.gettingOffline' : 'crew1102.getting'))) : {})}
    >
      <div className="page-head">
        <div>
          {/* 18 Oct 2026 (D-1018-HK): the Day is headed by the day itself — Today (the house home) greets once; the crew's
              Today is their home, so it keeps the greeting */}
          {isCrew ? (
            <>
              <div className="eyebrow">{formatDayKey(day, i18n.language)}</div>
              <h1 id="h-today" tabIndex={-1}>
                {t(greetingKey())}, {me.name}
              </h1>
            </>
          ) : (
            <>
              <div className="eyebrow" data-testid="day-context">{t('today.dayContext')}</div>
              <h1 id="h-today" tabIndex={-1} data-testid="day-heading">
                {formatDayKey(day, i18n.language)}
              </h1>
            </>
          )}
        </div>
        <div className="head-side">
          {!isCrew ? (
            <div className="legend" aria-hidden="true">
              <span>
                <i style={{ background: 'var(--st-conf-dot)' }} />
                {t('status.confirmed')}
              </span>
              <span>
                <i style={{ background: 'var(--st-sched-dot)' }} />
                {t('status.scheduled')}
              </span>
              <span>
                <i style={{ background: 'var(--st-hold-dot)' }} />
                {t('status.onHold')}
              </span>
              <span>
                <i style={{ background: 'var(--st-attn-dot)' }} />
                {t('status.attention')}
              </span>
            </div>
          ) : null}
          <div className="head-actions">
            <DayNav day={day} today={day} />
            {booking.canBook ? <RecentRequests /> : null}
            {booking.canBook ? <OutboxLink /> : null}
            <Updated at={q.dataUpdatedAt} fetching={q.isFetching && !q.isLoading} />
          </div>
        </div>
      </div>

      {/* 1 Nov 2026 (D-1102-CREW-1): the stored copy of Today is on the screen: said in words; nothing is pressed from it */}
      {fromCopy ? (
        <p className="today-stale-copy-note" role="status" data-testid="stale-copy-note">
          <Icon name="refresh" width={16} height={16} aria-hidden="true" />
          <span>{t(q0.isError ? 'crew1102.gettingOffline' : 'crew1102.getting')}</span>
        </p>
      ) : null}
      {isCrew ? <WaitingToSend /> : null}

      {/* 1.6.9: "Closed <day> — n to reschedule", at the very top, until everyone has a new day (office roles) */}
      {staff || me.role === 'scheduler' ? <ClosedWaitingLine days={q.data?.closedWaiting} /> : null}

      {isCrew ? (
        <div className="warn" style={{ marginBottom: 14 }} data-testid="crew-note">
          <Icon name="info" />
          <div>
            {crews ? (
              myLane ? (
                <b data-testid="crew-assignment">{t('crews166.youAre', { crew: myLane })} · </b>
              ) : null
            ) : crewExtras.data?.extras.crew ? (
              <b data-testid="crew-assignment">{t('today.crewAssignment', { crew: crewExtras.data.extras.crew })} · </b>
            ) : null}
            {crews && !myLane ? <span data-testid="crew-ask-office">{t('crews166.askOfficeNote')}</span> : t('today.crewNote')}
          </div>
        </div>
      ) : null}

      {isCrew && pendingCloseOut.length ? (
        <Card aria-labelledby="h-pending" className="pending-card" data-testid="pending-close-out">
          <CardHeader>
            <CardTitle id="h-pending">
              <Icon name="alert" /> {t('today.pendingCloseOut', { count: pendingCloseOut.length })}
              {lateCount ? ` · ${t('today.pendingLate', { count: lateCount })}` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="attn-list">{closeoutRows}</ul>
          </CardContent>
        </Card>
      ) : null}

      {/* 18 Oct 2026 (D-1018-HK): the office's ONE "Needs your attention", directly under the heading — the day's
          close-outs first, then the rest in the order the card always had; it ends with "Nothing else needs your attention" */}
      {!isCrew ? <AttentionCard top role={me.role!} staff={staff} tech="hide" listedToClose={listedToClose} openLong={openLong} closeouts={pendingCloseOut.length ? { count: pendingCloseOut.length, late: lateCount, rows: closeoutRows } : null} /> : null}

      <div className={isCrew ? 'today-grid crew' : lanes ? 'today-grid two-crews' : 'today-grid'}>
        <div className="stagger">
          {now ? (
            <div className="hero-now" data-testid="happening-now" role="region" aria-labelledby="h-now">
              <div className="eyebrow">{t('today.nowLabel')}</div>
              <h2 id="h-now">
                <span className="tnum">{now.unit}</span> · {visitName(now)}
              </h2>
              <div className="meta">
                <span>{t('today.hkVisit')}</span>
                <span aria-hidden="true">·</span>
                {/* 1.7.8: a card with a chosen start began when the crew began (its stamp), not at the planned slot */}
                <span>{t('today.started', { time: now.chosen && bend.windowOf(now.appointmentId) ? formatClock(bend.windowOf(now.appointmentId)!.start) : (timeOf(now.position) ?? '—') })}</span>
                {slot ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{t('today.crewOf', { n: crewNeeded ?? slot.crewSize })}</span>
                  </>
                ) : null}
                {now.cycleVisit ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      {now.cycleVisit.of ? t('today.cycleVisit', { n: now.cycleVisit.n, of: now.cycleVisit.of }) : t('today.cycleVisitNoLimit', { n: now.cycleVisit.n })}
                    </span>
                  </>
                ) : null}
              </div>
              <div className="actions">
                {closingOf(now) ? (
                  <WithScheduler closeout at={closingOf(now)!.at} testid="closing-now" />
                ) : finish.canFinish ? (
                  closesOut(now) ? (
                    <Button onClick={(e) => openFinish(now, e.currentTarget)} data-testid="finish-now">
                      <Icon name="check" />
                      {t('today.finishPerson')}
                    </Button>
                  ) : (
                    <Button onClick={() => finish.markCompleted(target(now))} data-testid="end-now">
                      <Icon name="check" />
                      {t('card168.end')}
                    </Button>
                  )
                ) : null}
                {now.callFirst ? <CallButton phone={now.callFirst.phone} name={now.callFirst.name} className="lg" /> : null}
                <CallButton phone={now.phone} name={now.tenantName} ghost className="lg" />
                {!isCrew && now.kind !== 'facility' ? (
                  <Link className="btn ghost" to={`/lookup?t=${encodeURIComponent(now.tenantId)}`}>
                    {t('today.openUnit', { unit: now.unit })}
                  </Link>
                ) : null}
              </div>
              <PressLines pre="now-" v={now} day={day} me={myEmail} canUndo={finish.canFinish} now={clock} />
              {now.callFirst ? <div className="meta contact-line">{callFirstLine(now)}</div> : null}
              {next ? (
                <div className="next-strip" data-testid="next-strip">
                  <Icon name="clock" width={18} height={18} />
                  <span>
                    <b>{t('today.next')}</b> · <span className="tnum">{timeOf(next.position)}</span> · {next.unit} · {visitName(next)} ·{' '}
                    {next.confirmation === 'Confirmed' ? t('status.confirmed') : t('status.awaiting')}
                  </span>
                </div>
              ) : null}
            </div>
          ) : next ? (
            <div className="next-card card" data-testid="next-strip">
              <div className="card-b">
                <Icon name="clock" width={18} height={18} />
                <span>
                  <b>{t('today.next')}</b> · <span className="tnum">{timeOf(next.position)}</span> · {next.unit} · {visitName(next)} ·{' '}
                  {next.confirmation === 'Confirmed' ? t('status.confirmed') : t('status.awaiting')}
                </span>
              </div>
            </div>
          ) : null}

          <Card aria-labelledby="h-visits" aria-busy={q.isLoading} style={{ marginTop: now || next ? 18 : 0 }}>
            <CardHeader>
              <CardTitle id="h-visits">
                {isCrew ? t('today.yourVisits') : t('today.visits')}
                {q.data ? (
                  <>
                    {' · '}
                    <span className="tnum">{visitCount}</span>
                  </>
                ) : null}
              </CardTitle>
              {slot && !isCrew ? (
                <span className="help" data-testid="slot-meta">
                  {t('today.crewNeeded', { count: crewNeeded ?? slot.crewSize })} · {t(`slotStatus.${slot.status}`, { defaultValue: slot.status })} ·{' '}
                  {t(`readiness.${slot.readiness}`, { defaultValue: slot.readiness })}
                </span>
              ) : null}
            </CardHeader>

            {q.isLoading ? (
              <CardContent>
                <p className="help">{t('app.loading')}</p>
              </CardContent>
            ) : q.isError ? (
              <CardContent>
                <div className="warn take" role="alert">
                  <Icon name="alert" />
                  <div>
                    <b>{t('today.error')}</b>
                    <div>{(q.error as Error).message}</div>
                    <div className="row">
                      <Button size="sm" onClick={() => q.refetch()}>
                        {t('today.retry')}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            ) : visits.length === 0 && !slot ? (
              <div className="empty" data-testid="today-empty">
                <Icon name="calendarEmpty" />
                <b>{t('today.emptyTitle')}</b>
                <span>{t('today.emptyHelp')}</span>
              </div>
            ) : (
              <>
                {!isCrew && sixToday >= warnAt ? (
                  <div className="warn" style={{ margin: '0 18px 12px' }} data-testid="today-six">
                    <Icon name="alert" />
                    <div>{lanes ? t('crews166.sevenBody', { n: sixToday }) : t('book.sixBody', { n: sixToday })}</div>
                  </div>
                ) : null}
                {dayPending.length ? (
                  <p className="help day-pending" style={{ margin: '0 18px 12px' }} data-testid="day-pending">
                    <PendingBadge tx={dayPending[0]!} /> {t('tx.dayPending', { count: dayPending.length, label: dayPending[0]!.label })}
                  </p>
                ) : null}
                {lanes && !isCrew ? (
                  <div className="today-lanes" data-testid="today-lanes">
                    {LANES.map((l) => {
                      const names = crews?.members[l] ?? [];
                      return (
                        <section key={l} className="today-lane" aria-labelledby={`h-lane-${l}`} data-testid={`today-lane-${l}`}>
                          <h3 className="list-h" id={`h-lane-${l}`}>
                            {names.length ? t('crews166.laneWith', { crew: l, names: names.join(', ') }) : t('crews166.laneTitle', { crew: l })}
                          </h3>
                          {names.length ? null : (
                            <p className="help lane-nobody" data-testid={`lane-nobody-${l}`}>
                              <Icon name="info" /> {t('crews166.nobody', { crew: l })}
                            </p>
                          )}
                          <ul aria-labelledby={`h-lane-${l}`} data-testid={`visit-list-${l}`}>
                            {listOf(lanePositions(lanes, l, positionCount), l, { open: true, gaps: true })}
                          </ul>
                        </section>
                      );
                    })}
                  </div>
                ) : (
                  <ul aria-label={isCrew ? (lanes && myLane ? t('crews166.yourLane', { crew: myLane }) : t('today.yourVisits')) : t('today.visits')} data-testid="visit-list">
                    {isCrew
                      ? listOf(mineFirst.map((v) => v.position!), lanes ? myLane : null, { open: false, gaps: true })
                      : listOf(Array.from({ length: positionCount }, (_, i) => i + 1), null, { open: true, gaps: true })}
                  </ul>
                )}
                {dayEndLine}
                {isCrew && notMine.length && otherCrew ? (
                  <>
                    <h3 className="list-h" id="h-not-yours" data-testid="not-yours-heading">
                      {t('crews166.otherLane', { crew: otherCrew })}
                    </h3>
                    <p className="help lane-nobody">{t('crews166.notYoursHelp')}</p>
                    <ul aria-labelledby="h-not-yours" data-testid="other-crew-list">
                      {listOf(notMine.map((v) => v.position!), otherCrew, { open: false, gaps: false })}
                    </ul>
                  </>
                ) : null}
                {facility.length ? (
                  <>
                    <h3 className="list-h" id="h-facility">
                      {t('today.facilities')}
                    </h3>
                    <ul aria-labelledby="h-facility" data-testid="facility-list">
                      {facility.map((v) => row(v, null))}
                    </ul>
                  </>
                ) : null}
                {!isCrew && unplaced.length ? (
                  <>
                    <h3 className="list-h" id="h-unplaced">
                      {t('today.alsoToday')}
                    </h3>
                    <ul aria-labelledby="h-unplaced" data-testid="unplaced-list">
                      {unplaced.map((v) => row(v, null))}
                    </ul>
                  </>
                ) : null}
              </>
            )}
          </Card>
        </div>

        {!isCrew ? (
          <div className="stagger">
            {/* 20 Oct 2026 (D-1020-EXTRA): an open spot today or in the coming days, for the Extra support list */}
            <ExtraSpotsCard role={me.role} />
            <LookAhead today={day} />
          </div>
        ) : null}
      </div>
      <Sheet open={!!arrivedFor} onOpenChange={(o) => !o && setArrivedFor(null)}>
        {arrivedFor ? <ArrivedSheet key={arrivedFor.appointmentId} visit={arrivedFor} time={timeOf(arrivedFor.position)} returnFocusTo={arrivedOpener} onDone={() => setArrivedFor(null)} /> : null}
      </Sheet>
      {/* 1.7.8: Move — another time today (the sheet asks before it pushes anyone, then says who needs to know) */}
      <Sheet open={!!moveFor} onOpenChange={(o) => !o && setMoveFor(null)}>
        {moveFor ? <MoveInDaySheet key={moveFor.appointmentId} visit={moveFor} window={moveWindow} gap={moveGap} returnFocusTo={moveOpener} onDone={() => setMoveFor(null)} /> : null}
      </Sheet>
    </section>
  );
}

/** The Outbox: what a press queued waits there for its cancel window (the scheduler can take it back). */
function OutboxLink() {
  const { t } = useTranslation();
  const q = useMessages('queued');
  const n = q.data?.counts.queued ?? 0;
  return (
    <Link to="/messages" className="btn secondary sm" data-testid="outbox-link">
      <Icon name="mail" />
      {n ? t('email.outboxWaiting', { count: n }) : t('email.outbox')}
    </Link>
  );
}
